// ============================================================================
// API - visualCircuitAPI.js
// ============================================================================
// ./api/visualCircuitAPI.js

import axiosInstance from '../utilities/axiosInstance';

const visualCircuitAPI = {
  // ============================================================================
  // CIRCUIT MANAGEMENT
  // ============================================================================

  // Get all circuits
  getAllCircuits: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/visual-circuit', { params });
      return response.data; // {success, data, pagination}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get circuits: ${error.message}` };
    }
  },

  // Get circuit by sessionId
  getCircuitBySessionId: async (sessionId) => {
    try {
      const response = await axiosInstance.get(`/api/visual-circuit/session/${sessionId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        error: `Failed to get circuit by sessionId: ${error.message}` 
      };
    }
  },

  // Get circuit by ID
  getCircuitById: async (id) => {
    try {
      const response = await axiosInstance.get(`/api/visual-circuit/${id}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get circuit: ${error.message}` };
    }
  },

  // Get circuit by circuitId
  getCircuitByCircuitId: async (circuitId) => {
    try {
      const response = await axiosInstance.get(`/api/visual-circuit/circuit/${circuitId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get circuit: ${error.message}` };
    }
  },

  // Create circuit
  createCircuit: async (data) => {
    try {
      const response = await axiosInstance.post('/api/visual-circuit', data);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to create circuit: ${error.message}` };
    }
  },

  // Update circuit
  updateCircuit: async (id, data) => {
    try {
      const response = await axiosInstance.put(`/api/visual-circuit/${id}`, data);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to update circuit: ${error.message}` };
    }
  },

  // Delete circuit
  deleteCircuit: async (id) => {
    try {
      const response = await axiosInstance.delete(`/api/visual-circuit/${id}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to delete circuit: ${error.message}` };
    }
  },

  // ============================================================================
  // CIRCUIT BY FILTERS
  // ============================================================================

  // Get circuits by student
  getCircuitsByStudent: async (studentId) => {
    try {
      const response = await axiosInstance.get(`/api/visual-circuit/student/${studentId}`);
      return response.data; // {success, data, count}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get circuits: ${error.message}` };
    }
  },

  // Get circuits by lab
  getCircuitsByLab: async (labId) => {
    try {
      const response = await axiosInstance.get(`/api/visual-circuit/lab/${labId}`);
      return response.data; // {success, data, count}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get circuits: ${error.message}` };
    }
  },

  // Get circuits by course
  getCircuitsByCourse: async (courseId, studentId) => {
      try {
        // console.log('🔍 DEBUG API: courseId =', courseId, 'studentId =', studentId); // ✅ ADD THIS
        
        const response = await axiosInstance.get(`/api/visual-circuit/course/${courseId}`, {
          params: { studentId }
        });
        
        // console.log('🔍 DEBUG API Response:', response.data); // ✅ ADD THIS
        
        return response.data;
      } catch (error) {
        return error?.response?.data || { success: false, error: `Failed to get circuits: ${error.message}` };
      }
  },

  // Get circuit by simulator
  getCircuitBySimulator: async (sessionId) => {
    try {
      const response = await axiosInstance.get(`/api/visual-circuit/simulator/${sessionId}`);
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get circuit: ${error.message}` };
    }
  },

  // ============================================================================
  // TEMPLATES
  // ============================================================================

  // Get templates
  getTemplates: async (category = null) => {
    try {
      const params = category ? { category } : {};
      const response = await axiosInstance.get('/api/visual-circuit/templates', { params });
      return response.data; // {success, data, count}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get templates: ${error.message}` };
    }
  },

  // ============================================================================
  // COMPONENT OPERATIONS
  // ============================================================================

  // Add component to circuit
  addComponent: async (id, component) => {
    try {
      const response = await axiosInstance.post(`/api/visual-circuit/${id}/component`, { component });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to add component: ${error.message}` };
    }
  },

  // Remove component from circuit
  removeComponent: async (id, instanceId) => {
    try {
      const response = await axiosInstance.delete(`/api/visual-circuit/${id}/component`, {
        data: { instanceId }
      });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to remove component: ${error.message}` };
    }
  },

  // ============================================================================
  // CONNECTION OPERATIONS
  // ============================================================================

  // Add connection to circuit
  addConnection: async (id, connection) => {
    try {
      const response = await axiosInstance.post(`/api/visual-circuit/${id}/connection`, { connection });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to add connection: ${error.message}` };
    }
  },

  // Remove connection from circuit
  removeConnection: async (id, connectionId) => {
    try {
      const response = await axiosInstance.delete(`/api/visual-circuit/${id}/connection`, {
        data: { connectionId }
      });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to remove connection: ${error.message}` };
    }
  },

  // ============================================================================
  // VALIDATION & SYNC
  // ============================================================================

  // Validate circuit
  validateCircuit: async (id) => {
    try {
      const response = await axiosInstance.post(`/api/visual-circuit/${id}/validate`);
      return response.data; // {success, data: {valid, errors}}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to validate circuit: ${error.message}` };
    }
  },

  // Sync circuit to simulator
  syncCircuitToSimulator: async (id) => {
    try {
      const response = await axiosInstance.post(`/api/visual-circuit/${id}/sync`);
      return response.data; // {success, message}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to sync circuit: ${error.message}` };
    }
  },

  // ============================================================================
  // CLONE & STATISTICS
  // ============================================================================

  // Clone circuit
  cloneCircuit: async (id, newStudentId) => {
    try {
      const response = await axiosInstance.post(`/api/visual-circuit/${id}/clone`, { newStudentId });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to clone circuit: ${error.message}` };
    }
  },

  // Get statistics
  getStatistics: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/visual-circuit/statistics', { params });
      return response.data; // {success, data}
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get statistics: ${error.message}` };
    }
  }
};

export default visualCircuitAPI;